import React from 'react';
import ReactPixel from 'react-facebook-pixel';
import ReactGA4 from 'react-ga4';

import { toDatetime } from 'src/legacy/Utils/Conversor';

import { postSellOpen, postSellClose, putSellEdit } from './api';
import { useEventData, useReferral } from './hooks';
import { InvalidFieldName, useCartStore, useCheckoutStore } from './stores';
import { cartStep } from 'src/utils/featureFlags';
import { addMinutes, differenceInMinutes, subSeconds } from 'date-fns';

interface IEventContext {
  openOrEditSell?: any;
  closeSell?: any;
  removeTicketFromCart?: any;
  addTicketToCart?: any;
  isNextButtonLoading?: any;
  setNextButtonLoading?: any;
  invalidFieldsNames: InvalidFieldName[];
  setInvalidFieldsNames?: React.Dispatch<
    React.SetStateAction<InvalidFieldName[]>
  >;
  invalidFieldsMessage: string[];
  setInvalidFieldsMessage?: React.Dispatch<React.SetStateAction<string[]>>;
}

const defaultState = {
  invalidFieldsNames: [],
  invalidFieldsMessage: [],
};

const EventContext = React.createContext<IEventContext>(defaultState);

const EventProvider: React.FC<{ id: string }> = ({ id, ...rest }) => {
  const { getEventCart, setEventCart } = useCartStore();
  const { getEventCheckout, clearEventCheckout, setEventCheckout } =
    useCheckoutStore();
  const referral = useReferral(id);
  const [invalidFieldsNames, setInvalidFieldsNames] = React.useState<
    InvalidFieldName[]
  >(defaultState.invalidFieldsNames);
  const [invalidFieldsMessage, setInvalidFieldsMessage] = React.useState<
    string[]
  >(defaultState.invalidFieldsMessage);
  const [isNextButtonLoading, setNextButtonLoading] = React.useState(false);

  const checkout = getEventCheckout(id);
  const event = useEventData();
  const removeTicketFromCart = React.useCallback(
    cartItemId => {
      const newCart = getEventCart(id)?.map(cartItem => {
        if (cartItem.ticketTypeId !== cartItemId) {
          return cartItem;
        }

        if (
          cartItem.quantity - cartStep(cartItem.ticketTypeId) < 0 ||
          new Date() > toDatetime(cartItem.sellEnd)
        ) {
          return cartItem;
        }

        return {
          ...cartItem,
          quantity: cartItem.quantity - cartStep(cartItem.ticketTypeId),
        };
      });
      setEventCart(id, newCart);
    },
    [getEventCart, id, setEventCart],
  );

  const addTicketToCart = React.useCallback(
    (cartItemId: number, eventName: string) => {
      const newCart = getEventCart(id)?.map(cartItem => {
        if (cartItem.ticketTypeId !== cartItemId) {
          return cartItem;
        }

        if (
          cartItem.quantity + cartStep(cartItem.ticketTypeId) >
          cartItem.availableQuantity ||
          new Date() > toDatetime(cartItem.sellEnd)
        ) {
          return cartItem;
        }

        const contents = { id, quantity: cartItem.quantity + 1 };
        console.log(event?.integrations?.pixelId);
        if (event?.integrations?.pixelId) {
          ReactPixel.trackSingle(event?.integrations?.pixelId, 'AddToCart', {
            content_type: 'product',
            content_ids: [id],
            value: cartItem.price,
            contents: contents,
            content_name: eventName,
            currency: 'BRL',
          });
        }
        const cartItemGA4 = {
          item_category: 'product',
          item_list_id: id,
          price: cartItem.price,
          quantity: 1,
          item_name: `${event?.name}'s`,
        };

        ReactGA4.event('add_to_cart', {
          currency: 'BRL',
          value: cartItem.price,
          eventName: event?.name,
          items: [cartItemGA4],
        });

        return {
          ...cartItem,
          quantity: cartItem.quantity + cartStep(cartItem.ticketTypeId),
        };
      });
      setEventCart(id, newCart);
    },
    [event?.integrations?.pixelId, event?.name, getEventCart, id, setEventCart],
  );

  const openSell = React.useCallback(
    async payload => {
      const response = await postSellOpen({
        payload,
        params: { event: id, ref: referral },
      });
      const now = new Date();
      const expirationTime = new Date(response.expirationTime);
      const openTime = new Date(response.openTime);

      const expirationTimeInMinutes = differenceInMinutes(
        expirationTime,
        openTime,
      );

      setEventCheckout(id, {
        openedDate: now.toISOString(),
        expirationDate: subSeconds(
          addMinutes(now, expirationTimeInMinutes),
          30,
        ).toISOString(),
        mediator: response.mediator,
        sellerKey: response.sellerKey ?? '',
        sellId: response.id,
      });
    },
    [id, referral, setEventCheckout],
  );

  const openOrEditSell = React.useCallback(async () => {
    const hasRecentExpiration = sessionStorage.getItem('checkout_expired') === 'true';
    // Isso evita que um checkout em uma aba seja reaberto automaticamente se expirou em outra aba
    const checkoutSpecificKey = checkout.sellId ? `checkout_expired_${checkout.sellId}` : null;
    const hasSpecificExpiration = checkoutSpecificKey ? sessionStorage.getItem(checkoutSpecificKey) === 'true' : false;
    // Se qualquer uma das flags estiver definida, impede a abertura automática
    if (hasRecentExpiration || hasSpecificExpiration) {
      console.log('Evitando abrir checkout após expiração recente');
      sessionStorage.removeItem('checkout_expired');
      if (checkoutSpecificKey) {
        sessionStorage.removeItem(checkoutSpecificKey);
      }
      return;
    }

    const payload = getEventCart(id).map(item => ({
      ticketTypeId: item.ticketTypeId,
      quantity: item.quantity,
    }));

    if (checkout.sellId) {
      await putSellEdit({ sellId: checkout.sellId, payload });
    } else {
      await openSell(payload);
    }
  }, [checkout.sellId, getEventCart, id, openSell]);

  const closeSell = React.useCallback(
    async sellId => {
      await postSellClose({ sellId });
      clearEventCheckout(id);
    },
    [clearEventCheckout, id],
  );

  const value = React.useMemo(
    () => ({
      openOrEditSell,
      closeSell,
      removeTicketFromCart,
      addTicketToCart,
      invalidFieldsNames,
      setInvalidFieldsNames,
      invalidFieldsMessage,
      setInvalidFieldsMessage,
      isNextButtonLoading,
      setNextButtonLoading,
    }),
    [
      addTicketToCart,
      closeSell,
      invalidFieldsMessage,
      invalidFieldsNames,
      isNextButtonLoading,
      openOrEditSell,
      removeTicketFromCart,
    ],
  );

  return <EventContext.Provider value={value} {...rest} />;
};

export const useEventContext = () => React.useContext(EventContext);

export default EventProvider;
